// components/ManageTenantSubscriptionModal.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, Calendar, CalendarDays, Ban, Save, 
    Gift, Play, AlertCircle
} from 'lucide-react';
import { useToast } from './ToastContainer';
import ConfirmDialog from './ConfirmDialog';

const ManageTenantSubscriptionModal = ({ 
  isOpen, 
  onClose, 
  tenant, 
  plans, 
  onSubmit, 
  onExtend, 
  onDeactivate,
  isSubmitting,
  onRefresh 
}) => {
  const { error: showError } = useToast();
  
  const [formData, setFormData] = useState({
    planId: '',
    status: 'active',
    billingCycle: 'yearly',
    specialPrice: '',
    isTrial: false,
    trialDays: '14',
    customPriceReason: '',
    action: 'activate'
  });

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    if (tenant && isOpen) {
      setFormData({
        planId: tenant.planId || '',
        status: tenant.status || 'active',
        billingCycle: tenant.billingCycle || 'yearly',
        specialPrice: tenant.actualPrice?.amount || '',
        isTrial: tenant.status === 'trial',
        trialDays: tenant.trialDays || '14',
        customPriceReason: tenant.priceReason || '',
        action: 'activate'
      });
      
      if (tenant.planId) {
        const plan = plans.find(p => p.planId === tenant.planId);
        setSelectedPlan(plan);
      }
    }
  }, [tenant, isOpen, plans]);

  if (!isOpen || !tenant) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (name === 'planId') {
      const plan = plans.find(p => p.planId === value);
      setSelectedPlan(plan);
      if (plan?.trial?.enabled) {
        setFormData(prev => ({
          ...prev,
          trialDays: plan.trial.durationDays || '14'
        }));
      }
    }
  };

  const selectedPlanDetails = selectedPlan || plans.find(p => p.planId === formData.planId);
  const defaultPrice = selectedPlanDetails?.pricing?.yearly?.amount || 0;
  const planHasTrial = selectedPlanDetails?.trial?.enabled || false;
  const planTrialDays = selectedPlanDetails?.trial?.durationDays || 14;
  const hasSpecialPrice = tenant?.actualPrice?.isDiscounted === true;
  const specialPriceAmount = tenant?.actualPrice?.amount || 0;



  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (formData.action === 'activate') {
      if (!formData.planId) {
        showError('Please select a plan');
        return;
      }
      
      onSubmit(tenant.tenantId, {
        planId: formData.planId,
        billingCycle: formData.billingCycle,
        useSpecialPrice: !!formData.specialPrice,
        action: 'activate'
      });
    } else if (formData.action === 'trial') {
      if (!formData.planId) {
        showError('Please select a plan');
        return;
      }
      
      onSubmit(tenant.tenantId, {
        planId: formData.planId,
        trialDays: parseInt(formData.trialDays) || 14,
        action: 'trial'
      });
    }
  };

  const handleDeactivateClick = () => {
    setConfirmAction('deactivate');
    setShowConfirm(true);
  };

  const handleExtendClick = (days) => {
    setConfirmAction({ type: 'extend', days });
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    if (confirmAction === 'deactivate') {
      onDeactivate(tenant.tenantId);
    } else if (confirmAction?.type === 'extend') {
      onExtend(tenant.tenantId, confirmAction.days, 'Manual extension');
    }
    setShowConfirm(false);
    setConfirmAction(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'text-emerald-600 bg-emerald-50 border-emerald-200',
      trial: 'text-blue-600 bg-blue-50 border-blue-200',
      expired: 'text-rose-600 bg-rose-50 border-rose-200',
      cancelled: 'text-slate-600 bg-slate-50 border-slate-200',
      suspended: 'text-red-600 bg-red-50 border-red-200',
      none: 'text-slate-400 bg-slate-50 border-slate-200'
    };
    return colors[status] || colors.none;
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl">
          {/* Header */}
          <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-slate-900 truncate">
                {tenant.tenantName || tenant.tenantId}
              </h2>
              <p className="text-sm text-slate-500 truncate">
                {tenant.hasSubscription ? 'Manage Subscription' : 'Assign Plan'}
                {hasSpecialPrice && (
                  <span className="ml-2 text-xs text-emerald-600 font-medium">
                    • Special Price: ₹{specialPriceAmount}
                  </span>
                )}
              </p>
            </div>
            <button 
              onClick={onClose} 
              className="flex-shrink-0 p-2 hover:bg-slate-100 rounded-xl transition-colors ml-2"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Current Status Badge */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-slate-500">Current Status:</span>
              <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(tenant.subscriptionStatus || 'none')}`}>
                {tenant.subscriptionStatus || 'No Subscription'}
              </span>
              {tenant.daysRemaining > 0 && tenant.subscriptionStatus !== 'none' && (
                <span className={`text-xs font-medium ${tenant.daysRemaining <= 7 ? 'text-rose-600' : 'text-slate-500'}`}>
                  {tenant.daysRemaining} days remaining
                </span>
              )}
            </div>

            {/* Action Selection */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                What would you like to do?
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, action: 'activate' }))}
                  className={`flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                    formData.action === 'activate'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Play className="w-4 h-4" />
                  Activate
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, action: 'trial' }))}
                  className={`flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                    formData.action === 'trial'
                      ? 'bg-amber-600 text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Gift className="w-4 h-4" />
                  Trial
                </button>
                {tenant.hasSubscription && (
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, action: 'extend' }))}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                      formData.action === 'extend'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Calendar className="w-4 h-4" />
                    Extend
                  </button>
                )}
              </div>
            </div>

            {/* Activation Form */}
            {(formData.action === 'activate' || formData.action === 'trial') && (
              <>
                {/* Plan Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Select Plan <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="planId"
                    value={formData.planId}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
                    required
                  >
                    <option value="">Select a plan</option>
                    {plans.filter(p => p.isActive).map((plan) => (
                      <option key={plan.planId} value={plan.planId}>
                        {plan.name} - {plan.pricing?.yearly?.amount === 0 ? 'Free' : `₹${plan.pricing?.yearly?.amount}/year`}
                        {plan.trial?.enabled && ` (${plan.trial.durationDays}d trial)`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Plan Details */}
                {selectedPlanDetails && (
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-xs text-slate-500">Plan Type</p>
                        <p className="font-medium text-slate-700 capitalize">{selectedPlanDetails.type}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Yearly Price</p>
                        <p className="font-medium text-slate-700">₹{selectedPlanDetails.pricing?.yearly?.amount || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Trial Available</p>
                        <p className="font-medium text-slate-700">{selectedPlanDetails.trial?.enabled ? 'Yes' : 'No'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Modules</p>
                        <p className="font-medium text-slate-700">
                          {Object.values(selectedPlanDetails.modules || {}).filter(v => v).length} modules
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Trial Specific Fields */}
                {formData.action === 'trial' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Trial Duration (Days)
                      </label>
                      <input
                        type="number"
                        name="trialDays"
                        value={formData.trialDays}
                        onChange={handleChange}
                        min="1"
                        max={planTrialDays}
                        className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                        placeholder="14"
                      />
                      {planHasTrial && (
                        <p className="text-xs text-slate-400 mt-1">Plan suggests {planTrialDays} days trial</p>
                      )}
                    </div>

                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-blue-700">
                          <span className="font-semibold">Trial Mode:</span> Tenant will have 
                          {selectedPlanDetails?.trial?.featuresDuringTrial === 'full' ? ' full ' : ' limited '}
                          access for {formData.trialDays || planTrialDays} days.
                        </p>
                      </div>
                    </div>
                  </>
                )}

                {/* Special Price */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Special Price (₹) - Optional
                  </label>
                  <input
                    type="number"
                    name="specialPrice"
                    value={formData.specialPrice}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                    placeholder="Leave empty for standard price"
                  />
                  {formData.specialPrice && parseFloat(formData.specialPrice) > 0 && defaultPrice > 0 && (
                    <div className="mt-2 text-xs text-emerald-600">
                      Discount: {Math.round(((defaultPrice - parseFloat(formData.specialPrice)) / defaultPrice) * 100)}% off
                    </div>
                  )}
                  {hasSpecialPrice && (
                    <div className="mt-2 text-xs text-amber-600">
                      Current special price: ₹{specialPriceAmount}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Reason (Optional)
                  </label>
                  <input
                    type="text"
                    name="customPriceReason"
                    value={formData.customPriceReason}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
                    placeholder="e.g., Volume discount, Beta customer..."
                  />
                </div>
              </>
            )}

            {/* Extend Section */}
            {formData.action === 'extend' && tenant.hasSubscription && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleExtendClick(30)}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-medium rounded-xl transition-colors border border-blue-200"
                  >
                    <Calendar className="w-4 h-4" />
                    +30 Days
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const days = prompt('Enter days to extend:', '30');
                      if (days && parseInt(days) > 0) {
                        handleExtendClick(parseInt(days));
                      }
                    }}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-sm font-medium rounded-xl transition-colors border border-indigo-200"
                  >
                    <CalendarDays className="w-4 h-4" />
                    Custom Extend
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleDeactivateClick}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-rose-50 hover:bg-rose-100 text-rose-700 text-sm font-medium rounded-xl transition-colors border border-rose-200"
                >
                  <Ban className="w-4 h-4" />
                  Deactivate Subscription
                </button>
              </div>
            )}
          </form>

          {/* Footer */}
          <div className="flex-shrink-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center gap-3 rounded-b-2xl">
            {(formData.action === 'activate' || formData.action === 'trial') && (
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                {isSubmitting ? 'Processing...' : formData.action === 'trial' ? 'Start Trial' : 'Activate Subscription'}
              </button>
            )}
            {formData.action === 'extend' && (
              <button
                type="button"
                onClick={() => {
                  const days = prompt('Enter days to extend:', '30');
                  if (days && parseInt(days) > 0) {
                    handleExtendClick(parseInt(days));
                  }
                }}
                disabled={isSubmitting}
                className="flex-1 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                Extend Subscription
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={showConfirm}
        onClose={() => {
          setShowConfirm(false);
          setConfirmAction(null);
        }}
        onConfirm={handleConfirm}
        title={confirmAction === 'deactivate' ? 'Deactivate Subscription' : `Extend by ${confirmAction?.days || 0} Days`}
        message={confirmAction === 'deactivate' 
          ? 'Are you sure you want to deactivate this subscription? The tenant will lose access immediately.'
          : `Are you sure you want to extend this subscription by ${confirmAction?.days || 0} days?`
        }
        confirmText={confirmAction === 'deactivate' ? 'Deactivate' : 'Confirm Extension'}
        type={confirmAction === 'deactivate' ? 'danger' : 'warning'}
      />
    </>
  );
};

export default ManageTenantSubscriptionModal;